/**
 * generateControlsXlsx.ts - Excel "Contrôles NIS365" STYLISÉ (xlsx-js-style).
 *
 *  Feuille "Dashboard" (vue prioritaire) :
 *    - bandeau de marque NIS365
 *    - GAUCHE : résumé par catégorie CyFun (Conforme / Partiel / À améliorer / …)
 *    - DROITE : résumé des contrôles par catégorie (M365 / Azure / Manuel / Custom)
 *  Feuille "CyFun"     : toutes les exigences CyFun (code, niveau, key, type, conformité, catégorie).
 *  Feuille "Contrôles" : inventaire de TOUS les contrôles avec statut coloré.
 */
import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import { confStats } from './cyfunReportData';

export type ControlSource = 'M365' | 'Azure' | 'Manuel' | 'Custom';

export interface XlsxControlRow { source: ControlSource; category: string; id: string; title: string; status: string; }
export interface XlsxCyFunRow { id: string; level: string; category: string; requirement: string; keyMeasure: boolean; type: string; conf: string; }
export interface XlsxMeta { orgName: string; date?: string; }

// --- Palette ---
const BRAND = '1F3D5C', ACCENT = '2E8B96', GREEN = '1F7A45', RED = 'B82329', ORANGE = 'A86317', SLATE = '4A5E72', GREY = '8A95A1', BAND = 'F2F5F8', WHITE = 'FFFFFF';

const CYFUN_STATUS = ['Conforme', 'Partiel', 'À améliorer', 'Non conforme', 'Non évalué'];
const CTRL_STATUS  = ['Conforme', 'Non conforme', 'À améliorer', 'Manuel', 'Non évalué'];
const STATUS_COLOR: Record<string, string> = {
  'Conforme': GREEN, 'Partiel': ACCENT, 'À améliorer': ORANGE, 'Non conforme': RED, 'Manuel': SLATE, 'Non évalué': GREY,
};
const TYPE_FR: Record<string, string> = { m365: 'Auto M365', azure: 'Auto Azure', manual: 'Manuel', external: 'Externe' };
function cyfunBucket(conf: string): string {
  if (conf === 'pass') return 'Conforme';
  if (conf === 'partial') return 'Partiel';
  if (conf === 'warning') return 'À améliorer';
  if (conf === 'fail') return 'Non conforme';
  return 'Non évalué';
}

const thin = { style: 'thin', color: { rgb: 'D6DCE4' } };
const allBorders = { top: thin, bottom: thin, left: thin, right: thin };

type Style = Record<string, unknown>;
function set(ws: XLSX.WorkSheet, r: number, c: number, value: string | number, s?: Style) {
  const addr = XLSX.utils.encode_cell({ r, c });
  ws[addr] = { t: typeof value === 'number' ? 'n' : 's', v: value };
  if (s) (ws[addr] as { s?: Style }).s = s;
}
const headStyle = (rgb: string): Style => ({ font: { bold: true, sz: 11, color: { rgb: WHITE } }, fill: { fgColor: { rgb } }, alignment: { horizontal: 'center', vertical: 'center', wrapText: true }, border: allBorders });

interface Group { label: string; counts: Record<string, number>; }
function groupCounts<T>(items: T[], keyFn: (x: T) => string, bucketFn: (x: T) => string): Group[] {
  const m = new Map<string, Record<string, number>>();
  const order: string[] = [];
  for (const it of items) {
    const k = keyFn(it);
    if (!m.has(k)) { order.push(k); m.set(k, {}); }
    const c = m.get(k)!; const b = bucketFn(it); c[b] = (c[b] ?? 0) + 1;
  }
  return order.map((k) => ({ label: k, counts: m.get(k)! }));
}

/** Écrit un tableau de synthèse à (startRow, startCol). Retourne la dernière ligne utilisée. */
function writeSummary(ws: XLSX.WorkSheet, startRow: number, startCol: number, title: string, statusCols: string[], groups: Group[], grandTotal: number, grandByStatus: Record<string, number>): number {
  set(ws, startRow, startCol, title, { font: { bold: true, sz: 12, color: { rgb: BRAND } } });
  const head = startRow + 1;
  set(ws, head, startCol, 'Catégorie', headStyle(BRAND));
  set(ws, head, startCol + 1, 'Total', headStyle(BRAND));
  statusCols.forEach((s, i) => set(ws, head, startCol + 2 + i, s, headStyle(STATUS_COLOR[s] ?? BRAND)));
  groups.forEach((g, gi) => {
    const r = head + 1 + gi;
    const band = gi % 2 === 1 ? { fill: { fgColor: { rgb: BAND } } } : {};
    const total = Object.values(g.counts).reduce((a, b) => a + b, 0);
    set(ws, r, startCol, g.label, { font: { sz: 10 }, border: allBorders, ...band });
    set(ws, r, startCol + 1, total, { font: { bold: true, sz: 10 }, alignment: { horizontal: 'center' }, border: allBorders, ...band });
    statusCols.forEach((s, j) => {
      const v = g.counts[s] ?? 0;
      set(ws, r, startCol + 2 + j, v, { font: { sz: 10, bold: v > 0, color: { rgb: v > 0 ? (STATUS_COLOR[s] ?? '333333') : 'B9C2CC' } }, alignment: { horizontal: 'center' }, border: allBorders, ...band });
    });
  });
  const tr = head + 1 + groups.length;
  const totS = (rgb = WHITE): Style => ({ font: { bold: true, sz: 11, color: { rgb } }, fill: { fgColor: { rgb: BRAND } }, alignment: { horizontal: 'center' }, border: allBorders });
  set(ws, tr, startCol, 'TOTAL', { ...totS(), alignment: { horizontal: 'left' } });
  set(ws, tr, startCol + 1, grandTotal, totS());
  statusCols.forEach((s, i) => set(ws, tr, startCol + 2 + i, grandByStatus[s] ?? 0, totS()));
  return tr;
}

export function downloadControlsXlsx(params: { controlRows: XlsxControlRow[]; cyfunRows: XlsxCyFunRow[]; meta: XlsxMeta }): void {
  const { controlRows, cyfunRows, meta } = params;
  const wb = XLSX.utils.book_new();
  const dateStr = (meta.date ? new Date(meta.date) : new Date()).toLocaleDateString('fr-BE');

  // ===================== Sheet Dashboard =====================
  const ws1: XLSX.WorkSheet = {};
  const RIGHT = 8; // colonne de départ du 2e tableau
  const lastCol = RIGHT + 6;
  set(ws1, 0, 0, 'NIS365 Auditor', { font: { bold: true, sz: 20, color: { rgb: WHITE } }, fill: { fgColor: { rgb: BRAND } }, alignment: { vertical: 'center' } });
  set(ws1, 1, 0, 'Tableau de bord de conformité', { font: { sz: 12, color: { rgb: WHITE } }, fill: { fgColor: { rgb: ACCENT } }, alignment: { vertical: 'center' } });
  set(ws1, 2, 0, `Organisation : ${meta.orgName}`, { font: { bold: true, sz: 11, color: { rgb: BRAND } } });
  set(ws1, 3, 0, `Généré le : ${dateStr}`, { font: { italic: true, sz: 10, color: { rgb: '6B7B8F' } } });

  // KPI — taux de conformité pondéré (Conforme = 1, Partiel/À améliorer = 0,5).
  const cyStats = confStats(cyfunRows);
  const scoreRgb = cyStats.score >= 80 ? GREEN : cyStats.score >= 50 ? ORANGE : RED;
  set(ws1, 4, 0, `Taux de conformité pondéré (CyFun) : ${cyStats.score}%  —  Conforme = 1, Partiel = 0,5, sur ${cyStats.evaluated} exigences évaluées (hors Manuel / Externe / Non évalué)`, { font: { bold: true, sz: 12, color: { rgb: scoreRgb } } });

  // GAUCHE : CyFun par catégorie
  const cyfunGroups = groupCounts(cyfunRows, (r) => r.category, (r) => cyfunBucket(r.conf));
  const cyfunGrand: Record<string, number> = {};
  for (const r of cyfunRows) { const b = cyfunBucket(r.conf); cyfunGrand[b] = (cyfunGrand[b] ?? 0) + 1; }
  const leftLast = writeSummary(ws1, 5, 0, 'Résumé CyFun par catégorie', CYFUN_STATUS, cyfunGroups, cyfunRows.length, cyfunGrand);

  // DROITE : Contrôles par catégorie
  const ctrlGroups = groupCounts(controlRows, (r) => `${r.source} · ${r.category}`, (r) => r.status);
  const ctrlGrand: Record<string, number> = {};
  for (const r of controlRows) ctrlGrand[r.status] = (ctrlGrand[r.status] ?? 0) + 1;
  const rightLast = writeSummary(ws1, 5, RIGHT, 'Contrôles par catégorie', CTRL_STATUS, ctrlGroups, controlRows.length, ctrlGrand);

  const lastRow = Math.max(leftLast, rightLast);
  ws1['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: lastRow, c: lastCol } });
  ws1['!cols'] = [{ wch: 26 }, { wch: 7 }, { wch: 11 }, { wch: 9 }, { wch: 12 }, { wch: 13 }, { wch: 11 }, { wch: 3 }, { wch: 26 }, { wch: 7 }, { wch: 11 }, { wch: 13 }, { wch: 12 }, { wch: 9 }, { wch: 11 }];
  ws1['!merges'] = [0, 1, 2, 3].map((r) => ({ s: { r, c: 0 }, e: { r, c: lastCol } }));
  ws1['!rows'] = [{ hpt: 28 }, { hpt: 20 }];
  XLSX.utils.book_append_sheet(wb, ws1, 'Dashboard');

  // ===================== Sheet CyFun =====================
  const wsC: XLSX.WorkSheet = {};
  set(wsC, 0, 0, 'NIS365 Auditor — Référentiel CyFun', { font: { bold: true, sz: 14, color: { rgb: WHITE } }, fill: { fgColor: { rgb: BRAND } }, alignment: { vertical: 'center' } });
  set(wsC, 1, 0, `Organisation : ${meta.orgName} · Généré le : ${dateStr}`, { font: { italic: true, sz: 10, color: { rgb: '6B7B8F' } } });
  const CH = 3;
  ['Code', 'Niveau', 'Key', 'Type', 'Conformité', 'Catégorie', 'Exigence'].forEach((h, i) =>
    set(wsC, CH, i, h, { font: { bold: true, sz: 11, color: { rgb: WHITE } }, fill: { fgColor: { rgb: BRAND } }, alignment: { horizontal: 'left', vertical: 'center' }, border: allBorders }));
  cyfunRows.forEach((row, i) => {
    const r = CH + 1 + i;
    const band = i % 2 === 1 ? { fill: { fgColor: { rgb: BAND } } } : {};
    const bucket = cyfunBucket(row.conf);
    set(wsC, r, 0, row.id, { font: { sz: 10, bold: true, name: 'Consolas' }, border: allBorders, ...band });
    set(wsC, r, 1, row.level, { font: { sz: 10 }, border: allBorders, ...band });
    set(wsC, r, 2, row.keyMeasure ? 'Oui' : '', { font: { sz: 10, bold: true, color: { rgb: ORANGE } }, alignment: { horizontal: 'center' }, border: allBorders, ...band });
    set(wsC, r, 3, TYPE_FR[row.type] ?? row.type, { font: { sz: 10 }, border: allBorders, ...band });
    set(wsC, r, 4, bucket, { font: { sz: 10, bold: true, color: { rgb: STATUS_COLOR[bucket] ?? '333333' } }, border: allBorders, ...band });
    set(wsC, r, 5, row.category, { font: { sz: 10 }, border: allBorders, ...band });
    set(wsC, r, 6, row.requirement, { font: { sz: 10 }, alignment: { wrapText: true }, border: allBorders, ...band });
  });
  const cLast = CH + Math.max(1, cyfunRows.length);
  wsC['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: cLast, c: 6 } });
  wsC['!cols'] = [{ wch: 14 }, { wch: 11 }, { wch: 6 }, { wch: 12 }, { wch: 16 }, { wch: 12 }, { wch: 80 }];
  wsC['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }, { s: { r: 1, c: 0 }, e: { r: 1, c: 6 } }];
  wsC['!rows'] = [{ hpt: 24 }];
  XLSX.utils.book_append_sheet(wb, wsC, 'CyFun');

  // ===================== Sheet Contrôles =====================
  const ws2: XLSX.WorkSheet = {};
  set(ws2, 0, 0, 'NIS365 Auditor — Inventaire des contrôles', { font: { bold: true, sz: 14, color: { rgb: WHITE } }, fill: { fgColor: { rgb: BRAND } }, alignment: { vertical: 'center' } });
  set(ws2, 1, 0, `Organisation : ${meta.orgName} · Généré le : ${dateStr}`, { font: { italic: true, sz: 10, color: { rgb: '6B7B8F' } } });
  const KH = 3;
  ['Source', 'Catégorie', 'ID', 'Titre', 'Statut'].forEach((h, i) =>
    set(ws2, KH, i, h, { font: { bold: true, sz: 11, color: { rgb: WHITE } }, fill: { fgColor: { rgb: BRAND } }, alignment: { horizontal: 'left', vertical: 'center' }, border: allBorders }));
  controlRows.forEach((row, i) => {
    const r = KH + 1 + i;
    const band = i % 2 === 1 ? { fill: { fgColor: { rgb: BAND } } } : {};
    set(ws2, r, 0, row.source, { font: { sz: 10, bold: true }, border: allBorders, ...band });
    set(ws2, r, 1, row.category, { font: { sz: 10 }, border: allBorders, ...band });
    set(ws2, r, 2, row.id, { font: { sz: 10, name: 'Consolas' }, border: allBorders, ...band });
    set(ws2, r, 3, row.title, { font: { sz: 10 }, alignment: { wrapText: true }, border: allBorders, ...band });
    set(ws2, r, 4, row.status, { font: { sz: 10, bold: true, color: { rgb: STATUS_COLOR[row.status] ?? '333333' } }, border: allBorders, ...band });
  });
  const kLast = KH + Math.max(1, controlRows.length);
  ws2['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: kLast, c: 4 } });
  ws2['!cols'] = [{ wch: 10 }, { wch: 24 }, { wch: 14 }, { wch: 64 }, { wch: 16 }];
  ws2['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }, { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } }];
  ws2['!rows'] = [{ hpt: 24 }];
  XLSX.utils.book_append_sheet(wb, ws2, 'Contrôles');

  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const safe = (meta.orgName || 'organisation').replace(/[^\w]+/g, '_');
  saveAs(blob, `NIS365_Controles_${safe}_${new Date().toISOString().slice(0, 10)}.xlsx`);
}
